import { ArrowUpRight } from "lucide-react";
import { scrollToId } from "../lib/scroll";
import { FOOTER_NAV } from "../lib/nav";
import { useLang } from "../lib/i18n";
import { LegalDialog, PrivacyDialog, CguDialog } from "./Policies";
import { CywizMark, CywizText } from "./CywizMark";
import { Kicker } from "./Shared";

const legalTrigger =
  "text-xs text-warm hover:text-cream transition-colors duration-300 underline underline-offset-4";

const labelFor = (item, t) =>
  item.labelKey === null ? <CywizMark /> : t.nav[item.labelKey];

/**
 * Pied de page — carte bordée cuivre, 3 colonnes + barre légale.
 */
export const Footer = () => {
  const { t } = useLang();
  const org = t.org;

  const NAV = FOOTER_NAV.map((item) => ({
    ...item,
    label: labelFor(item, t),
  }));

  return (
    <footer
      data-testid="site-footer"
      className="relative overflow-hidden bg-canvas px-4 sm:px-6 md:px-12 py-10 md:py-14"
    >
      <div className="max-w-site mx-auto rounded-2xl border border-copper-light/55 bg-canvas px-6 sm:px-8 md:px-12 py-10 md:py-12 lg:py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 lg:gap-16">
          {/* ---- Marque ---- */}
          <div>
            <div className="flex items-center gap-3">
              <img
                src="/images/aelvir_mark.webp"
                alt=""
                loading="lazy"
                decoding="async"
                className="h-12 sm:h-[3.25rem] w-auto"
              />
              <img
                src="/images/mot_aelvir.webp"
                alt="AËLVIR"
                loading="lazy"
                decoding="async"
                className="h-5 sm:h-6 w-auto"
              />
            </div>
            <p className="mt-5 text-[10px] sm:text-xs uppercase tracking-[0.28em] text-copper">
              {t.hero.motto.map((word, i) => (
                <span key={word}>
                  {i > 0 && (
                    <span className="text-white tracking-normal px-[0.55em]">
                      .
                    </span>
                  )}
                  {word}
                </span>
              ))}
            </p>
            <p className="mt-4 text-sm text-warm font-light max-w-sm leading-relaxed">
              <CywizText>{t.footerExtra.tagline}</CywizText>
            </p>
          </div>

          {/* ---- Navigation + produit ---- */}
          <div className="space-y-10">
            <div>
              <Kicker variant="footer">{t.footer.nav}</Kicker>
              <ul className="space-y-3">
                {NAV.map((n) => (
                  <li key={n.key}>
                    <a
                      href={n.href}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToId(n.href);
                      }}
                      className="text-sm text-warm hover:text-cream transition-colors duration-300"
                    >
                      {n.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <Kicker variant="footer">{t.footer.product}</Kicker>
              <a
                href="https://cywiz.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-warm hover:text-cream transition-colors duration-300"
              >
                cywiz.fr
                <span className="sr-only"> {t.common.newWindow}</span>
              </a>
            </div>
          </div>

          {/* ---- Coordonnées ---- */}
          <div>
            <Kicker variant="footer">{t.footer.contact}</Kicker>
            <address className="not-italic text-sm text-warm font-light space-y-1.5 leading-relaxed">
              <p>{org.name}</p>
              <p>{org.street}</p>
              <p>{org.city}</p>
              <a
                href={`mailto:${org.email}`}
                className="mt-3 inline-flex items-center gap-1.5 text-copper hover:text-copper-light transition-colors duration-300"
              >
                {org.email}
                <ArrowUpRight size={13} strokeWidth={1.75} aria-hidden />
              </a>
            </address>
          </div>
        </div>

        {/* ---- Barre légale ---- */}
        <div className="mt-12 md:mt-14 pt-6 border-t border-copper-light/25 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-dim">
            © {new Date().getFullYear()} {org.name}. {t.footer.rights}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2 text-xs text-warm">
            <LegalDialog triggerClassName={legalTrigger} />
            <span className="text-dim/70" aria-hidden>
              |
            </span>
            <PrivacyDialog
              testid="footer-privacy-trigger"
              triggerClassName={legalTrigger}
            />
            <span className="text-dim/70" aria-hidden>
              |
            </span>
            <CguDialog
              testid="footer-cgu-trigger"
              triggerClassName={legalTrigger}
            />
          </div>
        </div>
      </div>
    </footer>
  );
};
